<template>
    <div
        class="flex h-full gap-6"
        :class="[invertedForm ? 'flex-col-reverse' : 'flex-col']"
    >
        <DsMultiSelect
            v-if="allowTemplateSelection"
            v-model="fileDocumentStore.selectedDocumentTemplates"
            id="template-select"
            :disabled="isLoadingTemplates"
            label="Select from templates"
            :options="templateOptions"
            placeholder="Search templates..."
        />

        <DsUpload
            v-model:error="uploadError"
            v-model:fileList="stagedFiles"
            :dataTypes="acceptedDocumentTypes"
            :maxFileSize="maxFileSize"
            multiple
            :size="uploadAreaSize"
        >
            <template #clickTrigger="{ triggerFilePicker }">
                <slot
                    name="clickTrigger"
                    :triggerFilePicker
                />
            </template>
        </DsUpload>

        <div
            v-if="fileDocumentStore.firstLevelError || hasDocumentConfigs"
            class="grid gap-6"
        >
            <DsAlertBanner
                v-if="fileDocumentStore.firstLevelError"
                title="Please verify the eSign information"
                variant="error"
            >
                {{ fileDocumentStore.firstLevelError }}
            </DsAlertBanner>

            <div
                v-if="hasDocumentConfigs"
                class="flex flex-col gap-3"
            >
                <h3 class="text-content-md font-semibold text-content-primary">{{ headerText }}</h3>

                <div class="flex flex-col gap-3">
                    <DocumentUploadDeliverySetup
                        v-for="{ documentId } in documentConfigList"
                        :key="documentId"
                        :branchId
                        :clients
                        :documentId
                        :routes
                        @addClient="$emit('addClient')"
                    />
                </div>
            </div>
        </div>
        <DsCheckbox
            v-if="hasDocumentConfigs && enableNotificationFlag"
            v-model="sendNotification"
            label="Send notification to client(s)"
            name="sendNotification"
            value="notify"
        />
    </div>

    <dialog
        v-show="fileDocumentStore.isPreparingESign"
        ref="esignPreparingDialog"
        class="esign-preparing-dialog"
    >
        <div class="grid gap-2 text-center text-heading-md font-normal text-content-primary">
            <DsSpinner size="xxl" />
            <p>Your eSign document has opened in a new tab.</p>
            <p>Please complete your edits there before returning here to continue.</p>
        </div>
    </dialog>
</template>

<script setup lang="ts">
import DsMultiSelect from '@/components/design-system/multiselect/DsMultiSelect.vue';
import DsSpinner from '@/components/design-system/spinner/DsSpinner.vue';
import { DocumentDataType } from '@/components/design-system/upload/document-data-types';
import type { DsUploadClickTriggerSlotProps, DsUploadProps } from '@/components/design-system/upload/ds-upload.types';
import DsUpload from '@/components/design-system/upload/DsUpload.vue';
import type { ClientOptionInput, DocumentTemplateOption } from '@/components/Views/Company/File/Documents/document-upload-utils';
import { getTemplateOptions, validateDocumentSetup } from '@/components/Views/Company/File/Documents/document-upload-utils';
import { useDocumentUploadForm } from '@/components/Views/Company/File/Documents/useDocumentUploadForm';
import type { DocumentActionRoutes, DocumentUploadConfig } from '@/components/Views/Company/File/file.types';
import { toBytes } from '@/shared/utils/file-size-convert';
import { useFileDocumentStore } from '@/stores/file-document.store';
import type { Branch } from '@/types/closinglock.types';
import DsAlertBanner from '@ds/alert-banner/DsAlertBanner.vue';
import DsCheckbox from '@ds/form/input/DsCheckbox.vue';
import { computed, useTemplateRef, watch } from 'vue';
import DocumentUploadDeliverySetup from './DocumentUploadDeliverySetup.vue';

const props = withDefaults(
    defineProps<{
        branchId: Branch['id'];
        clients: ClientOptionInput[];
        routes: DocumentActionRoutes;
        active?: boolean;
        allowTemplateSelection?: boolean;
        dsUploadSize?: DsUploadProps['size'];
        invertedForm?: boolean;
        enableNotificationFlag?: boolean;
        headerText?: string;
    }>(),
    {
        active: true,
        headerText: 'Uploaded documents',
        enableNotificationFlag: true,
    },
);

const emit = defineEmits<{
    (e: 'success'): void;
    (e: 'addClient'): void;
}>();

defineSlots<{
    clickTrigger: DsUploadClickTriggerSlotProps;
}>();

const uploadAreaSize = computed<DsUploadProps['size']>(() => {
    if (props.dsUploadSize) {
        return props.dsUploadSize;
    }
    return hasDocumentConfigs.value ? 'md' : 'full';
});

const fileDocumentStore = useFileDocumentStore();
watch(
    () => props.routes,
    routes => {
        fileDocumentStore.routes = routes;
    },
    { immediate: true },
);

const esignPreparingDialog = useTemplateRef<HTMLDialogElement>('esignPreparingDialog');
const { stagedFiles, sendNotification, uploadError, resetForm } = useDocumentUploadForm({ esignPreparingDialog });

// Upload configuration
const acceptedDocumentTypes = [
    DocumentDataType.DOC,
    DocumentDataType.DOCX,
    DocumentDataType.PDF,
    DocumentDataType.JPEG,
    DocumentDataType.PNG,
];
const maxFileSize = toBytes(32, 'MB');

const { data: templates, execute: fetchTemplates, isFetching: isLoadingTemplates } = fileDocumentStore.fetchTemplates();

const templateOptions = computed<DocumentTemplateOption[]>(() => getTemplateOptions(templates.value?.data));

const initializeTemplates = () => {
    if (props.allowTemplateSelection) {
        fetchTemplates();
    }
};

const documentConfigList = computed<DocumentUploadConfig[]>(() => {
    return Array.from(fileDocumentStore.documentConfigs.values());
});
const hasDocumentConfigs = computed(() => !!documentConfigList.value.length);

watch(
    () => props.active,
    active => {
        active ? initializeTemplates() : resetForm();
    },
);

const submit = async () => {
    uploadError.value = false;
    if (!hasDocumentConfigs.value) {
        uploadError.value = 'Please upload at least one document.';
        return;
    }

    const { isValid, errors } = validateDocumentSetup(documentConfigList.value);
    if (!isValid) {
        errors.forEach(({ documentId, errors: docErrors }) => {
            fileDocumentStore.updateDocumentErrors(documentId, docErrors);
        });
        return;
    }

    const { success } = await fileDocumentStore.uploadDocuments(sendNotification.value);

    if (success) {
        emit('success');
        fileDocumentStore.updateFirstLevelError();
    }
};

defineExpose({
    submit,
});
</script>
<style scoped lang="scss">
.esign-preparing-dialog {
    @apply absolute left-0 top-0 grid h-screen w-screen place-items-center overflow-hidden bg-transparent;
    &,
    &::backdrop {
        animation-duration: var(--duration);
        animation-fill-mode: forwards;
    }

    &::backdrop {
        background-color: rgba(255, 255, 255, 0.7);
    }
}
</style>
